<template>
  <div class="p-6 bg-[#f9fafb] min-h-screen">

    <!-- ── Header ──────────────────────────────────────────────────────────── -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
      <h1 class="text-2xl font-bold text-gray-900">Racing Analytics Dashboard</h1>
      <!-- Active filter summary pill -->
      <span
        v-if="activeFilters"
        class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 border border-green-200 text-green-700 text-xs font-medium"
      >
        <span class="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span>
        {{ activeFilterLabel }}
      </span>
      <div class="flex flex-wrap items-center gap-2">
        <Datepicker
          v-model="dateRange"
          range
          format="dd MMM yyyy"
          :max-date="new Date()"
          :enable-time-picker="false"
          auto-apply
          :partial-range="false"
          placeholder="Select date range"
          :clearable="true"
          @update:model-value="onManualDateSelect"
          input-class-name="rounded-md border border-input bg-white px-3 py-1.5 text-sm"
        />
        <button
          v-for="btn in periodButtons"
          :key="btn.key"
          type="button"
          :class="[
            'px-3 py-1.5 text-xs font-medium border rounded-md transition-colors',
            activePeriod === btn.key
              ? 'bg-green-600 text-white border-green-600'
              : 'border-gray-300 bg-white hover:bg-gray-50 text-gray-700'
          ]"
          @click="btn.action"
        >{{ btn.label }}</button>
      </div>
    </div>

    <!-- ── Two-column layout: sidebar + main ────────────────────────────────── -->
    <div class="flex flex-col md:flex-row gap-6">

      <!-- Left sidebar -->
      <div class="w-full md:w-72 lg:w-80 shrink-0">
        <FilterSidebar ref="filterSidebarRef" :auto-apply="AUTO_APPLY" @change="onFiltersChanged" @apply="onFiltersChanged" />
      </div>

      <!-- Main content -->
      <div class="flex-1 min-w-0">

        <!-- Loading overlay -->
        <div v-if="isLoading" class="flex items-center justify-center py-20 text-gray-400">
          <svg class="animate-spin h-6 w-6 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
          </svg>
          <span class="text-sm">Loading analytics…</span>
        </div>

        <!-- API error -->
        <div v-else-if="apiError" class="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 mb-4">
          {{ apiError }}
        </div>

        <!-- No data yet -->
        <div v-else-if="!kpi" class="flex items-center justify-center py-20 text-gray-400 text-sm">
          No data available for the selected period.
        </div>

        <template v-else>

    <!-- ── KPI section – FreshBooks style ──────────────────────────────────── -->
    <!-- [A: 2×2 stats] [B: Winners comparison] [C: Strike Rate comparison] [D: 2×2 financials] -->
    <div class="flex gap-4 mb-6 items-stretch">

      <!-- A: Left 2×2 — race/runner counts -->
      <div class="grid grid-cols-2 gap-3 flex-1 min-w-[280px]">
        <div class="bg-white rounded-lg shadow-sm p-4 text-center min-w-0 overflow-hidden">
          <p class="text-sm text-gray-500 font-medium uppercase tracking-wide truncate">Total Races</p>
          <p class="text-2xl font-bold text-gray-900 mt-2 whitespace-nowrap">{{ kpi.totalRaces.toLocaleString() }}</p>
        </div>
        <div class="bg-white rounded-lg shadow-sm p-4 text-center min-w-0 overflow-hidden">
          <p class="text-sm text-gray-500 font-medium uppercase tracking-wide truncate">Total Runners</p>
          <p class="text-2xl font-bold text-gray-900 mt-2 whitespace-nowrap">{{ kpi.totalRunners.toLocaleString() }}</p>
        </div>
        <div class="bg-white rounded-lg shadow-sm p-4 text-center min-w-0 overflow-hidden">
          <p class="text-sm text-gray-500 font-medium uppercase tracking-wide truncate">Selections</p>
          <p class="text-2xl font-bold text-gray-900 mt-2 whitespace-nowrap">{{ kpi.totalSelections.toLocaleString() }}</p>
        </div>
        <div class="bg-white rounded-lg shadow-sm p-4 text-center min-w-0 overflow-hidden">
          <p class="text-sm text-gray-500 font-medium uppercase tracking-wide truncate">Winners</p>
          <p class="text-2xl font-bold text-gray-900 mt-2 whitespace-nowrap">{{ kpi.totalWinners.toLocaleString() }}</p>
        </div>
      </div>

      <!-- B: Winners — tall comparison card -->
      <div class="bg-white rounded-lg shadow-sm p-5 flex flex-col items-center w-44 shrink-0 overflow-hidden">
        <div class="flex items-center gap-1.5">
          <TrendingUp v-if="kpi.winnersChange >= 0" class="h-5 w-5 text-green-600 shrink-0" />
          <TrendingDown v-else class="h-5 w-5 text-red-500 shrink-0" />
          <span :class="['text-2xl font-bold whitespace-nowrap', kpi.winnersChange >= 0 ? 'text-green-600' : 'text-red-500']">
            {{ kpi.winnersChange >= 0 ? '+' : '' }}{{ Math.abs(kpi.winnersChange).toFixed(1) }}%
          </span>
        </div>
        <p class="text-sm text-gray-500 font-medium uppercase tracking-wide mt-1">Winners</p>
        <div class="flex items-center justify-around w-full mt-auto pt-4 border-t border-gray-100">
          <div class="text-center min-w-0">
            <p class="text-xl font-bold text-gray-900 whitespace-nowrap">{{ kpi.winnersThisMonth }}</p>
            <p class="text-sm text-gray-400 mt-1">This Month</p>
          </div>
          <div class="w-px h-10 bg-gray-200 shrink-0 mx-2" />
          <div class="text-center min-w-0">
            <p class="text-xl font-bold text-gray-400 whitespace-nowrap">{{ kpi.winnersLastMonth }}</p>
            <p class="text-sm text-gray-400 mt-1">Last Month</p>
          </div>
        </div>
      </div>

      <!-- C: Strike Rate — tall comparison card -->
      <div class="bg-white rounded-lg shadow-sm p-5 flex flex-col items-center w-44 shrink-0 overflow-hidden">
        <div class="flex items-center gap-1.5">
          <TrendingUp v-if="kpi.strikeRateChange >= 0" class="h-5 w-5 text-green-600 shrink-0" />
          <TrendingDown v-else class="h-5 w-5 text-red-500 shrink-0" />
          <span :class="['text-2xl font-bold whitespace-nowrap', kpi.strikeRateChange >= 0 ? 'text-green-600' : 'text-red-500']">
            {{ kpi.strikeRateChange >= 0 ? '+' : '' }}{{ kpi.strikeRateChange.toFixed(1) }}pp
          </span>
        </div>
        <p class="text-sm text-gray-500 font-medium uppercase tracking-wide mt-1">Strike Rate</p>
        <div class="flex items-center justify-around w-full mt-auto pt-4 border-t border-gray-100">
          <div class="text-center min-w-0">
            <p class="text-xl font-bold text-gray-900 whitespace-nowrap">{{ kpi.strikeRateThisMonth }}%</p>
            <p class="text-sm text-gray-400 mt-1">This Month</p>
          </div>
          <div class="w-px h-10 bg-gray-200 shrink-0 mx-2" />
          <div class="text-center min-w-0">
            <p class="text-xl font-bold text-gray-400 whitespace-nowrap">{{ kpi.strikeRateLastMonth }}%</p>
            <p class="text-sm text-gray-400 mt-1">Last Month</p>
          </div>
        </div>
      </div>

      <!-- D: Right 2×2 — financial metrics -->
      <div class="grid grid-cols-2 gap-3 flex-1 min-w-[280px]">
        <div class="bg-white rounded-lg shadow-sm p-4 text-center min-w-0 overflow-hidden">
          <p class="text-sm text-gray-500 font-medium uppercase tracking-wide truncate">Total P&amp;L</p>
          <p :class="['text-3xl font-bold mt-2 whitespace-nowrap', kpi.totalPnl >= 0 ? 'text-green-600' : 'text-red-500']">
            {{ kpi.totalPnl >= 0 ? '+' : '-' }}£{{ Math.abs(kpi.totalPnl).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
          </p>
        </div>
        <div class="bg-white rounded-lg shadow-sm p-4 text-center min-w-0 overflow-hidden">
          <p class="text-sm text-gray-500 font-medium uppercase tracking-wide truncate">ROI</p>
          <p :class="['text-3xl font-bold mt-2 whitespace-nowrap', kpi.roi >= 0 ? 'text-green-600' : 'text-red-500']">
            {{ kpi.roi >= 0 ? '+' : '' }}{{ kpi.roi.toFixed(1) }}%
          </p>
        </div>
        <div class="bg-white rounded-lg shadow-sm p-4 text-center min-w-0 overflow-hidden">
          <p class="text-sm text-gray-500 font-medium uppercase tracking-wide truncate">Win Rate</p>
          <p class="text-2xl font-bold text-gray-900 mt-2 whitespace-nowrap">{{ kpi.winRate.toFixed(1) }}%</p>
        </div>
        <div class="bg-white rounded-lg shadow-sm p-4 text-center min-w-0 overflow-hidden">
          <p class="text-sm text-gray-500 font-medium uppercase tracking-wide truncate">Avg Monthly P&amp;L</p>
          <p :class="['text-3xl font-bold mt-2 whitespace-nowrap', kpi.avgMonthlyProfit >= 0 ? 'text-green-600' : 'text-red-500']">
            {{ kpi.avgMonthlyProfit >= 0 ? '+' : '-' }}£{{ Math.abs(kpi.avgMonthlyProfit).toFixed(2) }}
          </p>
        </div>
      </div>

    </div>

    <!-- ── Charts Row 1 ─────────────────────────────────────────────────────── -->
    <div class="flex flex-col lg:flex-row gap-4 mb-4">

      <!-- Monthly Selections & Winners (grouped bar) -->
      <div class="bg-white rounded-lg shadow-sm p-5 flex-1 min-w-0">
        <h2 class="text-sm font-semibold text-gray-700">Monthly Selections &amp; Winners</h2>
        <p class="text-xs text-gray-400 mt-0.5">{{ periodLabel }}</p>
        <div class="mt-4" style="height: 256px; position: relative;">
          <canvas ref="barChartCanvas"></canvas>
        </div>
      </div>

      <!-- Top 5 Courses by Profit (horizontal bar) -->
      <div class="bg-white rounded-lg shadow-sm p-5 w-full lg:w-96 shrink-0">
        <h2 class="text-sm font-semibold text-gray-700">Top 5 Courses by Profit</h2>
        <p class="text-xs text-gray-400 mt-0.5">{{ periodLabel }}</p>
        <div class="mt-4" style="height: 256px; position: relative;">
          <canvas ref="coursesChartCanvas"></canvas>
        </div>
      </div>

    </div>

    <!-- ── Charts Row 2 ─────────────────────────────────────────────────────── -->
    <div class="flex flex-col lg:flex-row gap-4">

      <!-- Top 5 Trainers by Strike Rate (horizontal bar) -->
      <div class="bg-white rounded-lg shadow-sm p-5 w-full lg:w-96 shrink-0">
        <h2 class="text-sm font-semibold text-gray-700">Top 5 Trainers by Strike Rate</h2>
        <p class="text-xs text-gray-400 mt-0.5">{{ periodLabel }}</p>
        <div class="mt-4" style="height: 256px; position: relative;">
          <canvas ref="trainersChartCanvas"></canvas>
        </div>
      </div>

      <!-- Monthly Profit Trend (line + average line) -->
      <div class="bg-white rounded-lg shadow-sm p-5 flex-1 min-w-0">
        <h2 class="text-sm font-semibold text-gray-700">Monthly Profit Trend</h2>
        <p class="text-xs text-gray-400 mt-0.5">{{ periodLabel }}</p>
        <div class="mt-4" style="height: 256px; position: relative;">
          <canvas ref="trendChartCanvas"></canvas>
        </div>
      </div>

    </div>

        </template> <!-- /v-else kpi -->

      </div> <!-- /main content -->
    </div>   <!-- /two-column layout -->

  </div>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { Chart, registerables } from 'chart.js';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { TrendingUp, TrendingDown } from 'lucide-vue-next';
import FilterSidebar from '@/components/FilterSidebar.vue';
import apiService from '@/services/api.service';

Chart.register(...registerables);

// ─── Filter mode toggle ────────────────────────────────────────────────────────
// true  → dashboard re-renders automatically on every filter change (auto-apply)
// false → user must click "Apply Filters" to refresh (manual apply)
const AUTO_APPLY = true;

// ─── Colours ──────────────────────────────────────────────────────────────────
const GREEN     = '#16a34a';
const GREEN_BG  = 'rgba(22, 163, 74, 0.75)';
const ORANGE    = '#f97316';
const ORANGE_BG = 'rgba(249, 115, 22, 0.75)';
const GRID_LINE = 'rgba(0, 0, 0, 0.05)';

export default {
  name: 'AnalyticsDashboard',
  components: { Datepicker, TrendingUp, TrendingDown, FilterSidebar },

  setup() {
    // ── Date range ─────────────────────────────────────────────────────────
    const dateRange    = ref(null);
    const activePeriod = ref('6m');

    const applyRange = (months, key) => {
      const end   = new Date();
      const start = new Date();
      start.setMonth(start.getMonth() - months);
      dateRange.value    = [start, end];
      activePeriod.value = key;
    };

    const setLast3Months = () => applyRange(3,  '3m');
    const setLast6Months = () => applyRange(6,  '6m');
    const setLastYear    = () => applyRange(12, '1y');

    const onManualDateSelect = () => { activePeriod.value = ''; };

    const periodButtons = [
      { key: '3m', label: '3M', action: setLast3Months },
      { key: '6m', label: '6M', action: setLast6Months },
      { key: '1y', label: '1Y', action: setLastYear   },
    ];

    const periodLabel = computed(() => {
      if (!dateRange.value?.[0]) return 'Last 6 Months';
      const fmt = d => d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
      return `${fmt(dateRange.value[0])} – ${fmt(dateRange.value[1] ?? new Date())}`;
    });

    // ── API state ──────────────────────────────────────────────────────────
    const analyticsData    = ref(null);
    const isLoading        = ref(false);
    const apiError         = ref(null);
    const currentFilters   = ref({});
    const activeFilters    = ref(false);
    const activeFilterLabel = ref('');

    // ── KPI — computed from live API data ──────────────────────────────────
    const kpi = computed(() => {
      const stats = analyticsData.value?.statistics;
      const tm    = analyticsData.value?.thisMonth;
      const lm    = analyticsData.value?.lastMonth;
      if (!stats) return null;

      const winnersChange = lm?.winners > 0
        ? parseFloat((((tm?.winners - lm?.winners) / lm?.winners) * 100).toFixed(1))
        : 0;

      return {
        totalRaces:          stats.totalRaces,
        totalRunners:        stats.totalRunners,
        totalSelections:     stats.totalSelections,
        totalWinners:        stats.totalWinners,
        totalPnl:            stats.totalProfitLoss,
        roi:                 stats.roi,
        winRate:             stats.strikeRate,
        avgMonthlyProfit:    stats.avgMonthlyProfit,
        winnersThisMonth:    tm?.winners     ?? 0,
        winnersLastMonth:    lm?.winners     ?? 0,
        winnersChange,
        strikeRateThisMonth: tm?.strikeRate  ?? 0,
        strikeRateLastMonth: lm?.strikeRate  ?? 0,
        strikeRateChange:    parseFloat(((tm?.strikeRate ?? 0) - (lm?.strikeRate ?? 0)).toFixed(1)),
        thisMonthPeriod:     tm?.period      ?? '',
        lastMonthPeriod:     lm?.period      ?? '',
      };
    });

    // ── Chart refs & instances ─────────────────────────────────────────────
    const filterSidebarRef    = ref(null);
    const barChartCanvas      = ref(null);
    const coursesChartCanvas  = ref(null);
    const trainersChartCanvas = ref(null);
    const trendChartCanvas    = ref(null);

    let barChart, coursesChart, trainersChart, trendChart;

    const destroyCharts = () => {
      [barChart, coursesChart, trainersChart, trendChart].forEach(c => c?.destroy());
    };

    const initCharts = (data) => {
      destroyCharts();

      const monthly  = data.monthlyStats         ?? [];
      const courses  = data.topCoursesByProfit   ?? [];
      const trainers = data.topTrainersByStrikeRate ?? [];

      const months     = monthly.map(m => m.month);
      const selections = monthly.map(m => m.selections);
      const winners    = monthly.map(m => m.winners);
      const profit     = monthly.map(m => m.return);
      const avg        = profit.length > 0
        ? profit.reduce((a, b) => a + b, 0) / profit.length : 0;

      // 1 ── Monthly Selections & Winners — grouped bar ─────────────────────
      barChart = new Chart(barChartCanvas.value.getContext('2d'), {
        type: 'bar',
        data: {
          labels: months,
          datasets: [
            {
              label: 'Selections',
              data: selections,
              backgroundColor: GREEN_BG,
              borderColor: GREEN,
              borderWidth: 1,
              borderRadius: 3,
            },
            {
              label: 'Winners',
              data: winners,
              backgroundColor: ORANGE_BG,
              borderColor: ORANGE,
              borderWidth: 1,
              borderRadius: 3,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'top', labels: { usePointStyle: true, boxWidth: 6, padding: 16 } },
            tooltip: { mode: 'index', intersect: false },
          },
          scales: {
            x: { grid: { display: false } },
            y: { beginAtZero: true, grid: { color: GRID_LINE } },
          },
        },
      });

      // 2 ── Top 5 Courses — horizontal bar ─────────────────────────────────
      coursesChart = new Chart(coursesChartCanvas.value.getContext('2d'), {
        type: 'bar',
        data: {
          labels: courses.map(c => c.course),
          datasets: [{
            data: courses.map(c => c.profitLoss),
            backgroundColor: courses.map(c => c.profitLoss >= 0 ? GREEN_BG : 'rgba(239,68,68,0.6)'),
            borderColor:     courses.map(c => c.profitLoss >= 0 ? GREEN     : '#ef4444'),
            borderWidth: 1,
            borderRadius: 3,
          }],
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          onClick: (_, elements) => {
            if (!elements.length) return;
            const c = courses[elements[0].index];
            if (c?.courseId) filterSidebarRef.value?.addCourse({ id: c.courseId, value: c.course });
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: ctx => {
                  const v = ctx.parsed.x;
                  return ` ${v >= 0 ? '+' : ''}£${v.toFixed(2)}`;
                },
              },
            },
          },
          scales: {
            x: { grid: { color: GRID_LINE }, ticks: { callback: v => `£${v}` } },
            y: { grid: { display: false } },
          },
        },
      });

      // 3 ── Top 5 Trainers — horizontal bar ────────────────────────────────
      trainersChart = new Chart(trainersChartCanvas.value.getContext('2d'), {
        type: 'bar',
        data: {
          labels: trainers.map(t => t.trainer),
          datasets: [{
            data: trainers.map(t => t.strikeRate),
            backgroundColor: ORANGE_BG,
            borderColor: ORANGE,
            borderWidth: 1,
            borderRadius: 3,
          }],
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          onClick: (_, elements) => {
            if (!elements.length) return;
            const t = trainers[elements[0].index];
            if (t?.trainerId) filterSidebarRef.value?.addTrainer({ id: t.trainerId, value: t.trainer });
          },
          plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: ctx => ` ${ctx.parsed.x.toFixed(1)}%` } },
          },
          scales: {
            x: {
              beginAtZero: true,
              grid: { color: GRID_LINE },
              ticks: { callback: v => `${v}%` },
            },
            y: { grid: { display: false } },
          },
        },
      });

      // 4 ── Monthly Profit Trend — line + dashed average ───────────────────
      trendChart = new Chart(trendChartCanvas.value.getContext('2d'), {
        type: 'line',
        data: {
          labels: months,
          datasets: [
            {
              label: 'Monthly Profit',
              data: profit,
              borderColor: GREEN,
              backgroundColor: 'rgba(22, 163, 74, 0.08)',
              borderWidth: 2.5,
              pointBackgroundColor: GREEN,
              pointRadius: 4,
              fill: true,
              tension: 0.1,
            },
            {
              label: 'Average',
              data: Array(months.length).fill(parseFloat(avg.toFixed(2))),
              borderColor: 'rgba(239, 68, 68, 0.65)',
              borderWidth: 1.5,
              borderDash: [6, 4],
              pointRadius: 0,
              fill: false,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'top', labels: { usePointStyle: true, boxWidth: 6, padding: 16 } },
            tooltip: {
              mode: 'index',
              intersect: false,
              callbacks: {
                label: ctx => {
                  const v = ctx.parsed.y;
                  if (ctx.dataset.label === 'Average') return ` Avg: £${v.toFixed(2)}`;
                  return ` ${ctx.dataset.label}: ${v >= 0 ? '+' : ''}£${v.toFixed(2)}`;
                },
              },
            },
          },
          scales: {
            x: { grid: { display: false } },
            y: { grid: { color: GRID_LINE }, ticks: { callback: v => `£${v}` } },
          },
        },
      });
    };

    // ── API fetch ──────────────────────────────────────────────────────────
    const fetchAnalytics = async () => {
      if (!dateRange.value?.[0]) return;
      isLoading.value = true;
      apiError.value  = null;
      try {
        const fmt = d => d.toISOString().split('T')[0];
        const params = {
          StartDate: fmt(dateRange.value[0]),
          EndDate:   fmt(dateRange.value[1] ?? new Date()),
          ...currentFilters.value,
        };
        const response       = await apiService.getAnalytics(params);
        analyticsData.value  = response.data;
      } catch (e) {
        apiError.value = 'Failed to load analytics data. Please try again.';
        console.error('[Analytics] API error:', e);
      } finally {
        isLoading.value = false;
      }
    };

    // Re-render charts whenever the API data updates.
    // nextTick ensures the canvas elements are in the DOM before Chart.js accesses them
    // (the canvases live inside <template v-else> which only mounts after isLoading → false).
    watch(analyticsData, async (data) => {
      if (data) {
        await nextTick();
        initCharts(data);
      }
    });

    // Re-fetch when the date range changes (covers both quick buttons and datepicker)
    watch(dateRange, () => { fetchAnalytics(); });

    onMounted(() => { setLast6Months(); });   // triggers the dateRange watcher → fetchAnalytics
    onUnmounted(destroyCharts);

    // ── Filter change handler ──────────────────────────────────────────────
    const onFiltersChanged = (filters) => {
      currentFilters.value = filters;
      const total = Object.values(filters).reduce((sum, arr) => sum + arr.length, 0);
      activeFilters.value     = total > 0;
      activeFilterLabel.value = total > 0 ? `${total} filter${total > 1 ? 's' : ''} active` : '';
      fetchAnalytics();
    };

    return {
      AUTO_APPLY,
      dateRange,
      activePeriod,
      periodButtons,
      periodLabel,
      onManualDateSelect,
      kpi,
      isLoading,
      apiError,
      activeFilters,
      activeFilterLabel,
      onFiltersChanged,
      filterSidebarRef,
      barChartCanvas,
      coursesChartCanvas,
      trainersChartCanvas,
      trendChartCanvas,
      TrendingUp,
      TrendingDown,
    };
  },
};
</script>
